import { useCallback, useRef } from 'react'

import { useAuthedFetch } from '../../auth/useAuthedFetch'
import withFetchCancel from '../../libs/withFetchCancel'
import { useMutation, useQuery } from 'react-query'
import { transformFiltersToQueryParams } from './business'
import { useUserContext } from '../../auth/UserProvider'

const BACKEND_ENDPOINT = process.env.REACT_APP_BACKEND_ENDPOINT

const SHIPPINGS_RECORDING_KEY = 'shippingsRecording'

const useShippingsRecordingTracking = ({
  client_code,
  filters,
  config = {},
  allowCancelled,
}) => {
  const queryParams = transformFiltersToQueryParams(filters, allowCancelled)

  const authedFetch = useAuthedFetch()

  const refetchRef = useRef()

  const queryKey = [SHIPPINGS_RECORDING_KEY]

  const query = useQuery(
    queryKey,
    () => {
      if (client_code) {
        const fetchPromise = withFetchCancel(signal =>
          authedFetch(
            'GET',
            `${BACKEND_ENDPOINT}/v1.0/${client_code}/shippings/manifest-documents?${queryParams}`,
            {
              parseJSON: true,
              signal,
            },
          ),
        )
        refetchRef.current = () => fetchPromise
        return fetchPromise
      } else {
        return []
      }
    },
    {
      select: ({ data }) => data,
      ...config,
    },
  )

  const manualRefetch = useCallback(() => {
    if (refetchRef.current) {
      refetchRef.current()
    }
  }, [])

  return { ...query, manualRefetch }
}

const useDeleteFileMutation = (onSuccess, onError) => {
  const authedFetch = useAuthedFetch()

  return useMutation(
    ({ clientCode, id }) =>
      withFetchCancel(signal =>
        authedFetch(
          'DELETE',
          `${BACKEND_ENDPOINT}/v1.0/${clientCode}/shippings/manifest-documents/${id}`,
          {
            signal,
            data: { clientCode, id },
          },
        ),
      ),
    {
      useErrorBoundary: false,
      onSuccess: () => onSuccess(),
      onError: () => onError(),
    },
  )
}

const useGetFileDetailMutation = (onSuccess, onError) => {
  const authedFetch = useAuthedFetch()

  return useMutation(
    ({ clientCode, id, provider }) =>
      withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/v1.0/${clientCode}/shippings/manifest-documents/rpc-download-document`,
          {
            signal,
            parseJSON: true,
            data: {
              resource_provider: provider,
              resource_id: id.startsWith('/') ? id.substring(1) : id,
            },
          },
        ),
      ),
    {
      useErrorBoundary: false,
      onSuccess: e => onSuccess(e?.data),
      onError: () => onError(),
    },
  )
}

const useUploadFiles = clientId => {
  const authedFetch = useAuthedFetch()
  const encodedClientId = encodeURIComponent(clientId)

  return useMutation(
    async file => {
      const response = await withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/v1.0/${encodedClientId}/shippings/manifest-documents/rpc-upload-file`,
          {
            parseJSON: true,
            signal,
            data: file,
          },
        ),
      )

      return response.data
    },
    {
      onSuccess: (data, variables) => {},
      useErrorBoundary: false,
    },
  )
}

const useManifestFiles = (clientId, onSuccess, onError) => {
  const authedFetch = useAuthedFetch()
  const encodedClientId = encodeURIComponent(clientId)
  const { webUserEmail } = useUserContext()

  return useMutation(
    async ({ file, initialPickupDetails, clientCenterCode }) => {
      const response = await withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/v1.0/${encodedClientId}/shippings/rpc-manifest-document`,
          {
            parseJSON: true,
            signal,
            data: {
              manifested_by: webUserEmail ?? '',
              resource_provider: file?.resource_provider,
              resource_id: file?.resource_id,
              file_name: file?.fileBodyPOST?.file_name,
              file_extension: file?.fileBodyPOST?.file_extension,
              file_size: file?.fileBodyPOST?.file_size,
              compression_type: file?.fileBodyPOST?.compression_type,
              md5: file?.fileBodyPOST?.md5,
              initial_pickup_details: {
                ...initialPickupDetails,
                client_center_code: clientCenterCode,
              },
            },
          },
        ),
      )

      return response.data
    },
    {
      onSuccess: () => onSuccess(),
      onError: () => onError(),
      useErrorBoundary: false,
    },
  )
}

export {
  useShippingsRecordingTracking,
  useDeleteFileMutation,
  useGetFileDetailMutation,
  useUploadFiles,
  useManifestFiles,
}
